/**
 * Map Clustering Utilities
 * 
 * Converts pin clusters to ClusterInfo format for map visualization
 */

import type { ClusterInfo } from '@/types/mapReferences';
import type { MapPin } from '@/components/maps/MapView';
import type { PinCluster } from './pinFiltering';
import { calculateDistance } from '@/services/location';
import type { LocationCoordinates } from '@/services/location';

/**
 * Convert PinCluster to ClusterInfo format
 */
export const convertToClusterInfo = (
  cluster: PinCluster,
  options?: {
    label?: string;
    color?: string;
  }
): ClusterInfo => {
  // Calculate radius as the maximum distance from center to any pin in cluster
  const radius = cluster.pins.reduce((maxRadius, pin) => {
    const distance = calculateDistance(cluster.coordinates, pin.coordinates);
    return Math.max(maxRadius, distance);
  }, 0);

  // Generate label if not provided
  const label = options?.label || 
    (cluster.pinCount > 1 
      ? `${cluster.pinCount} activities` 
      : undefined);

  return {
    id: cluster.id,
    pins: cluster.pins.map(pin => pin.id),
    center: cluster.coordinates,
    radius: Math.max(radius, 200), // Minimum 200m radius for visibility
    label,
    color: options?.color,
  };
};

/**
 * Generate cluster labels based on time of day or category
 */
export const generateClusterLabel = (
  cluster: PinCluster,
  options?: {
    groupByTime?: boolean;
    groupByCategory?: boolean;
  }
): string => {
  if (cluster.pinCount === 0) return '';

  if (options?.groupByTime) {
    // Group by time of day
    const times = cluster.pins
      .filter(pin => pin.type === 'itinerary')
      .map(pin => {
        const item = pin.data as any;
        if (item?.startTime) {
          const [hours] = item.startTime.split(':').map(Number);
          if (hours < 12) return 'morning';
          if (hours < 17) return 'afternoon';
          return 'evening';
        }
        return null;
      })
      .filter(Boolean) as string[];

    if (times.length > 0) {
      const timeGroup = times[0];
      const capitalized = timeGroup.charAt(0).toUpperCase() + timeGroup.slice(1);
      return `${capitalized} Activities (${cluster.pinCount})`;
    }
  }

  if (options?.groupByCategory) {
    // Group by most common category
    const categories = cluster.pins
      .map(pin => pin.category)
      .filter(Boolean) as string[];
    
    if (categories.length > 0) {
      const categoryCounts = categories.reduce((acc, cat) => {
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      const mostCommon = Object.entries(categoryCounts)
        .sort(([, a], [, b]) => b - a)[0]?.[0];
      
      if (mostCommon) {
        const capitalized = mostCommon.charAt(0).toUpperCase() + mostCommon.slice(1);
        return `${capitalized} (${cluster.pinCount})`;
      }
    }
  }

  // Default label
  return cluster.pinCount > 1 ? `${cluster.pinCount} activities` : '';
};

